'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Slide } from '@mui/material';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@/primitives';
import MenuIcon from '@mui/icons-material/Menu';
import { NAV_HEIGHT } from '@/configs/layout';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { useIsPublicSearch } from '@/hooks/useIsPublicSearch';
import SocietySearchTabMobileSelect from '@/components/publicSearch/searchPage/SocietySearchTabMobileSelect';
import Navigation from './Navigation';

const HEADER_TRANSITION_MS = 280;

export default function MobileToolbar() {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const isSocietySearch = useIsPublicSearch();

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: 1.25,
          px: '12px',
          py: '6px',
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          height: `${NAV_HEIGHT}px`,
          backgroundColor: 'background.paper',
          boxShadow: 'none',

          '@media print': {
            display: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ flexShrink: 0, minWidth: 0 }}
        >
          <Link
            href={PUBLIC_ROUTES.HOME}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
              minWidth: 0,
              textDecoration: 'none',
            }}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Image
                alt="BreedLOGIC mark"
                height={14}
                width={10}
                src="/breedlogic-shell-logo.svg"
                style={{ objectFit: 'contain', width: '10px', height: '14px' }}
              />
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              BreedLOGIC
            </Typography>
          </Link>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ flexShrink: 0, ml: 'auto', maxWidth: '60%' }}
        >
          <Slide
            appear
            direction={isSocietySearch ? 'down' : 'up'}
            timeout={HEADER_TRANSITION_MS}
            in
            key={
              isSocietySearch
                ? 'mobile-bar-center-society'
                : 'mobile-bar-center-default'
            }
          >
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isSocietySearch ? (
                <SocietySearchTabMobileSelect />
              ) : (
                <Box sx={{ width: 0 }} />
              )}
            </Box>
          </Slide>
        </Stack>
        <IconButton
          size="small"
          aria-label="Open menu"
          onClick={() => setDrawerOpen(true)}
          sx={{
            width: 40,
            height: 40,
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            flexShrink: 0,
          }}
        >
          <MenuIcon sx={{ color: 'text.primary', fontSize: 22 }} />
        </IconButton>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        sx={{
          flexShrink: 0,

          '& .MuiDrawer-paper': {
            p: 0,
            width: '90%',
            maxWidth: '320px',
            boxSizing: 'border-box',
            borderRadius: 0,
            padding: '12px 10px 10px',
          },

          '@media print': {
            display: 'none',
          },
        }}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: '100%' }}>
          <Navigation
            onNavigate={() => setDrawerOpen(false)}
            onClose={() => setDrawerOpen(false)}
          />
        </Box>
      </Drawer>
    </>
  );
}
